<script>

	import { tick } from 'svelte';
	import { fly } from 'svelte/transition';

	import { clickOutside } from '../clickoutside.js';

	export let field;
	export let response = null;

	let open = false;

	function openOptions() {
		open = true;
	}

	function toggleOptions() {
		open = !open;
	}

	// 19 May 2026
	// We need these timeouts because Edge fires the button's blur event before the list item's click event

	let closer;

	function closeOptions(e) {
		clearTimeout(closer);
		closer = setTimeout(() => {
			open = false;
		}, 100);
	}

	function keepOptions(e) {
		setTimeout(() => {
			clearTimeout(closer);
		}, 50);
	}

	let filterText = '';
	let filtered;

	$: if (!open) filterText = '';
	$: filtered = filterOptions(filterText);

	function filterOptions(txt) {
		let opts = [];
		for (const o of field.options) {
			if ((txt == '') || (o.label.EN.toLowerCase().includes(txt.toLowerCase()))) {
				opts.push(o);
			}
		}
		return opts.length ? opts : field.options;
	}

	let placeholder;
	$: placeholder = getNameFromValue(response);

	function getNameFromValue(v) {
		// console.log('getNameFromValue', v);
		if (!v) return '';
		for (const o of field.options) {
			if (o.ref == v) {
				return o.name.EN;
			}
		}
		return '';
	}

	let highlighted = -1;
	let hovering = false;

	$: if (!open) {
		highlighted = -1;
		hovering = false;
	}

	function handleOptionClick(e) {
		setValue(e.target.dataset.value);
	}

	async function setValue(v) {
		if (v === undefined) v = null;
		response = v;
		open = false;
		highlighted = -1;
		await tick();
	}

	let newFilter = true;
	let newFilterTimeout;

	function handleKeyDown(e) {
		switch (e.key) {
			case "ArrowDown":
			case "ArrowRight":
				e.stopPropagation();
				e.preventDefault();
				openOptions();
				if (!hovering && (highlighted != filtered.length)) {
					highlighted++;
					if ((highlighted == 0) && (!showNull)) {
						highlighted = 1;
					}
					setSheetScroll();
				}
				break;
			case "ArrowUp":
				e.stopPropagation();
				e.preventDefault();
				openOptions();
				if (!hovering) {
					if (highlighted == -1) {
						highlighted = filtered.length;
					} else if (highlighted != (showNull ? 0 : 1)) {
						highlighted--;
					}
					setSheetScroll();
				}
				break;
			case "Enter":
				e.stopPropagation();
				e.preventDefault();
				if (highlighted == -1) {
					openOptions();
				} else if (highlighted == 0) {
					setValue(undefined);
				} else if (filtered[highlighted - 1]) {
					setValue(filtered[highlighted - 1].value);
				}
				break;
			case "Escape":
				e.stopPropagation();
				e.preventDefault();
				if (filtered.length != displayOptions.length) {
					filterText = '';
				} else {
					closeOptions();
					highlighted = -1;
				}
				break;
			default:
				if ((e.which >= 48) && (e.which <= 90)) {
					e.stopPropagation();
					e.preventDefault();
					openOptions();
					clearTimeout(newFilterTimeout);
					highlighted = -1;
					if (newFilter) {
						filterText = String.fromCharCode(e.which);
						newFilter = false;
					} else {
						filterText = filterText + String.fromCharCode(e.which);
					}
					newFilterTimeout = setTimeout(function(){
						newFilter = true;
					}, 800);
					break;
				}
		}
	}

	let container;
	let sheet;
	let sheetOverflow = false;

	$: if (sheet) {
		sheetOverflow = opensInOverflow();
	}

	function setSheetScroll() {
		const lit = document.getElementById(id + '_opt_' + highlighted);
		if (lit) {
			const scroll = sheet.scrollTop;
			const sheetHeight = sheet.offsetHeight;
			const litTop = lit.offsetTop;
			const litHeight = lit.offsetHeight;
			if ((litTop + litHeight) > (sheetHeight - scroll)) {
				const newScroll = litTop;
				sheet.scrollTop = newScroll;
			} else if (litTop < scroll) {
				const newScroll = litTop;
				sheet.scrollTop = newScroll;
			}
		}
	}

	function getPanel(el) {
		for (; el; el = el.offsetParent) {
			if (el.classList.contains('signup_form')) {
				return el;
			}
		}
	}

	function abstop(el, boundary) {
		let y = 0;
		for (; el; el = el.offsetParent) {
			y += el.offsetTop;
			if (boundary && (el == boundary)) break;
		}
		return y;
	}

	function opensInOverflow() {
		const panel = getPanel(container);
		const scroll = panel ? panel.scrollTop : 0;
		const top = abstop(container, panel) - scroll;
		const panelHeight = panel ? panel.offsetHeight : 0;
		const containerHeight = container.offsetHeight;
		const sheetHeight = sheet.offsetHeight;
		const absBottom = (top + containerHeight + sheetHeight);
		const viewportOffset = container.getBoundingClientRect();
		const viewportBottom = viewportOffset.bottom + sheetHeight;
		const out = (absBottom > panelHeight) || (viewportBottom > (window.innerHeight || document.documentElement.clientHeight));
		return out;
	}

</script>

<div class="Select" bind:this={container}>
	<button
		type="button"
		id={'s_' + field.ref}
		class:open
		on:click={toggleOptions}
		on:blur={closeOptions}
		on:keydown|stopPropagation={handleKeyDown}
		role="listbox"
		aria-haspopup="true"
		aria-expanded={open}
		aria-owns={open ? 's_' + field.ref + '_options' : undefined}
		aria-activedescendant={(highlighted == -1) ? undefined : 's_' + field.ref + '_opt_' + highlighted}
	>
		<span>{ placeholder }</span>
		<svg viewBox="0 0 100 100"><path d="m68 36c-1.3 0-2.6-.5-3.5-1.5l-13.8-13.7c-.4-.4-1-.4-1.4 0l-13.8 13.8c-2.1 2.1-5.5 1.9-7.4-.4-1.7-2-1.4-5 .5-6.9l17.9-17.9c2-2 5.1-2 7.1 0l17.9 17.9c2 2 2.1 5.4 0 7.4-1 .9-2.2 1.3-3.5 1.3z"/><path d="m50 92c-1.3 0-2.6-.5-3.5-1.5l-17.9-17.8c-1.8-1.8-2.1-4.8-.5-6.9 1.9-2.3 5.3-2.4 7.4-.4l13.8 13.8c.4.4 1 .4 1.4 0l13.8-13.8c2-2 5.1-2 7.1 0s2 5.1 0 7.1l-18 18c-1 1-2.3 1.5-3.6 1.5z"/></svg>
	</button>
	{#if open}
		<div
			id="s_{field.ref}_options"
			class="options"
			bind:this={sheet}
			class:low={sheetOverflow}
			transition:fly|local={{ duration: 200, y: -3 }}
			on:mouseover={() => {hovering = true}}
			on:mouseout={() => {hovering = false}}
		>
			<ul>
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<li
					id="s_{field.ref}_opt_0"
					data-value={null}
					class:highlighted={highlighted == 0}
					role="option"
					on:mousedown={keepOptions}
					on:click={handleOptionClick}
					on:mouseup={closeOptions}
					on:mouseover={() => {highlighted = 0}}
				>
				</li>
				{#each filtered as o, i (o.ref)}
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<li
						id="s_{field.ref}_opt_{i+1}"
						data-value={o.ref}
						class:highlighted={highlighted == i + 1}
						role="option"
						on:mousedown={keepOptions}
						on:click={handleOptionClick}
						on:mouseup={closeOptions}
						on:mouseover={() => {highlighted = i + 1}}
					>
						{o.name.EN}
					</li>
				{/each}
			</ul>
		</div>
	{/if}
</div>